import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export type PreReqUploadType = 'Organogram' | 'BusinessDocument';
export type PreReqUploadStatus = 'Uploaded' | 'Processing' | 'Completed' | 'Failed';

export interface OrganogramRole {
  id: string;
  preRecUploadId: string;
  role: string;
  description: string;
  level: number;
  permissions: string[];
  review: boolean | null;
  createdAt: string;
}

export interface BusinessClassification {
  id: string;
  preRecUploadId: string;
  classification: string;
  evidenceText: string;
  referenceText: string | null;
  review: boolean | null;
  createdAt: string;
}

export interface PermissionCatalogItem {
  id: string;
  code: string;
  displayName: string;
  description: string;
  aliases: string[];
  category: string;
  review: boolean | null;
  source: string;
  createdAt: string;
}

export interface PreReqUpload {
  id: string;
  type: PreReqUploadType;
  fileName: string;
  fileSizeInBytes: number;
  status: PreReqUploadStatus;
  roles: OrganogramRole[];
  classifications: BusinessClassification[];
  errorMessage: string | null;
  createdAt: string;
  processedAt: string | null;
}

export interface PreReqUploadListData {
  total: number;
  page: number;
  pageSize: number;
  items: PreReqUpload[];
}

export interface ApiResponse<T> {
  statusCode: number;
  success: boolean;
  message: string;
  data: T;
}

@Injectable({
  providedIn: 'root'
})
export class PreReq {
  // Backend routes/DTOs are still named PreRec (out of scope for this rename), so the path stays as-is.
  private baseUrl = `${environment.apiUrl}/pre-rec`;

  constructor(private http: HttpClient) {}

  upload(type: PreReqUploadType, files: File[]): Observable<ApiResponse<PreReqUpload[]>> {
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file, file.name);
    }
    return this.http.post<ApiResponse<PreReqUpload[]>>(`${this.baseUrl}/uploads?type=${type}`, formData);
  }

  getUploads(type: PreReqUploadType, page: number = 1, pageSize: number = 50): Observable<ApiResponse<PreReqUploadListData>> {
    return this.http.get<ApiResponse<PreReqUploadListData>>(
      `${this.baseUrl}/uploads?type=${type}&page=${page}&pageSize=${pageSize}`);
  }

  /** Retries a failed upload against the already-stored file — no re-upload needed. */
  retryUpload(id: string): Observable<ApiResponse<PreReqUpload>> {
    return this.http.post<ApiResponse<PreReqUpload>>(`${this.baseUrl}/uploads/${id}/retry`, {});
  }

  // Organogram roles
  createRole(
    preRecUploadId: string,
    role: string,
    description: string,
    level: number,
    permissions: string[]
  ): Observable<ApiResponse<OrganogramRole>> {
    return this.http.post<ApiResponse<OrganogramRole>>(`${this.baseUrl}/organogram-roles`, {
      preRecUploadId, role, description, level, permissions
    });
  }

  updateRole(
    id: string,
    role: string,
    description: string,
    level: number,
    permissions: string[]
  ): Observable<ApiResponse<OrganogramRole>> {
    return this.http.put<ApiResponse<OrganogramRole>>(`${this.baseUrl}/organogram-roles/${id}`, {
      role, description, level, permissions
    });
  }

  reviewRole(id: string, review: boolean): Observable<ApiResponse<OrganogramRole>> {
    return this.http.put<ApiResponse<OrganogramRole>>(`${this.baseUrl}/organogram-roles/${id}/review`, { review });
  }

  deleteRole(id: string): Observable<ApiResponse<{ id: string }>> {
    return this.http.delete<ApiResponse<{ id: string }>>(`${this.baseUrl}/organogram-roles/${id}`);
  }

  // Permission catalog
  listPermissions(): Observable<ApiResponse<PermissionCatalogItem[]>> {
    return this.http.get<ApiResponse<PermissionCatalogItem[]>>(`${this.baseUrl}/permissions`);
  }

  createPermission(
    displayName: string,
    description: string,
    code: string | null,
    aliases: string[],
    category: string = 'other'
  ): Observable<ApiResponse<PermissionCatalogItem>> {
    return this.http.post<ApiResponse<PermissionCatalogItem>>(`${this.baseUrl}/permissions`, {
      displayName, description, code, aliases, category
    });
  }

  updatePermission(
    id: string,
    displayName: string,
    description: string,
    code: string | null,
    aliases: string[],
    category: string = 'other'
  ): Observable<ApiResponse<PermissionCatalogItem>> {
    return this.http.put<ApiResponse<PermissionCatalogItem>>(`${this.baseUrl}/permissions/${id}`, {
      displayName, description, code, aliases, category
    });
  }

  reviewPermission(id: string, review: boolean): Observable<ApiResponse<PermissionCatalogItem>> {
    return this.http.put<ApiResponse<PermissionCatalogItem>>(`${this.baseUrl}/permissions/${id}/review`, { review });
  }

  deletePermission(id: string): Observable<ApiResponse<{ id: string }>> {
    return this.http.delete<ApiResponse<{ id: string }>>(`${this.baseUrl}/permissions/${id}`);
  }

  bulkReviewPermissions(ids: string[], review: boolean): Observable<ApiResponse<PermissionCatalogItem[]>> {
    return this.http.post<ApiResponse<PermissionCatalogItem[]>>(`${this.baseUrl}/permissions/bulk-review`, {
      ids, review
    });
  }

  bulkDeletePermissions(ids: string[]): Observable<ApiResponse<{ deleted: number }>> {
    return this.http.post<ApiResponse<{ deleted: number }>>(`${this.baseUrl}/permissions/bulk-delete`, { ids });
  }

  // Business classifications
  createClassification(preRecUploadId: string, classification: string, evidenceText: string, referenceText: string | null): Observable<ApiResponse<BusinessClassification>> {
    return this.http.post<ApiResponse<BusinessClassification>>(`${this.baseUrl}/business-classifications`, { preRecUploadId, classification, evidenceText, referenceText });
  }

  updateClassification(id: string, classification: string, evidenceText: string, referenceText: string | null): Observable<ApiResponse<BusinessClassification>> {
    return this.http.put<ApiResponse<BusinessClassification>>(`${this.baseUrl}/business-classifications/${id}`, { classification, evidenceText, referenceText });
  }

  reviewClassification(id: string, review: boolean): Observable<ApiResponse<BusinessClassification>> {
    return this.http.put<ApiResponse<BusinessClassification>>(`${this.baseUrl}/business-classifications/${id}/review`, { review });
  }

  deleteClassification(id: string): Observable<ApiResponse<{ id: string }>> {
    return this.http.delete<ApiResponse<{ id: string }>>(`${this.baseUrl}/business-classifications/${id}`);
  }
}
